<template>
	<v-dialog
		:value = "value"
		max-width = "980"
		persistent
		data-testid = "dashboard-pie-events-dialog"
		@input = "updateValue"
	>
		<v-card data-testid = "dashboard-pie-events-dialog-card" outlined>
			<v-card-title class = "primary white--text body-1 py-2">
				<v-icon small class = "mr-2" color = "white">mdi-chart-box-outline</v-icon>
				<span>{{ $ml.get('dashboard_pie_events_title') }}</span>
				<v-spacer></v-spacer>
				<v-btn icon small dark @click = "close">
					<v-icon small>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-card-text class = "pt-4">
				<v-alert dense outlined type = "info" class = "caption mb-4">
					{{ $ml.get('dashboard_pie_events_shell_help') }}
				</v-alert>

				<v-progress-linear
					v-if = "loadingMetadata"
					indeterminate
					color = "primary"
					class = "mb-3"
				></v-progress-linear>

				<v-alert
					v-if = "metadataError"
					dense
					outlined
					type = "warning"
					class = "caption mb-4"
				>
					{{ $ml.get('dashboard_pie_events_metadata_error') }}
				</v-alert>

				<v-alert
					v-if = "validationErrors.length"
					dense
					outlined
					type = "error"
					class = "caption mb-4"
				>
					<div v-for = "error in validationErrors" :key = "error">
						{{ $ml.get(error) }}
					</div>
				</v-alert>

				<v-row dense>
					<v-col cols = "12">
						<v-card outlined>
							<v-card-title class = "caption text-uppercase py-2">
								<v-icon x-small class = "mr-1">mdi-chart-box-outline</v-icon>
								{{ $ml.get('dashboard_pie_events_section_chart') }}
							</v-card-title>
							<v-card-text class = "py-2">
								<v-row dense>
									<v-col cols = "12" md = "6">
										<v-text-field
											dense
											outlined
											clearable
											:label = "$ml.get('dashboard_pie_events_chart_name')"
											v-model = "form.name"
										></v-text-field>
									</v-col>
									<v-col cols = "12" md = "6">
										<v-select
											dense
											outlined
											:items = "chartTypeItems"
											item-text = "label"
											item-value = "key"
											:label = "$ml.get('dashboard_pie_events_chart_type')"
											v-model = "form.chartType"
										></v-select>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>

					<v-col cols = "12" md = "6">
						<v-card outlined class = "fill-height">
							<v-card-title class = "caption text-uppercase py-2">
								<v-icon x-small class = "mr-1">mdi-database</v-icon>
								{{ $ml.get('dashboard_pie_events_section_datasource') }}
							</v-card-title>
							<v-card-text class = "py-2">
								<v-select
									dense
									outlined
									:disabled = "loadingMetadata"
									:items = "datasourceItems"
									item-text = "label"
									item-value = "key"
									:label = "$ml.get('dashboard_pie_events_datasource')"
									v-model = "form.datasource"
								></v-select>
							</v-card-text>
						</v-card>
					</v-col>

					<v-col cols = "12" md = "6">
						<v-card outlined class = "fill-height">
							<v-card-title class = "caption text-uppercase py-2">
								<v-icon x-small class = "mr-1">mdi-clock-outline</v-icon>
								{{ $ml.get('dashboard_pie_events_section_interval') }}
							</v-card-title>
							<v-card-text class = "py-2">
								<v-select
									dense
									outlined
									:items = "intervalPresetItems"
									item-text = "label"
									item-value = "key"
									:label = "$ml.get('dashboard_pie_events_interval_preset')"
									v-model = "form.time.preset"
								></v-select>
								<v-row dense v-if = "isCustomRange">
									<v-col cols = "12" md = "6">
										<v-text-field
											dense
											outlined
											type = "datetime-local"
											:label = "$ml.get('dashboard_pie_events_from')"
											v-model = "form.time.custom.from"
										></v-text-field>
									</v-col>
									<v-col cols = "12" md = "6">
										<v-text-field
											dense
											outlined
											type = "datetime-local"
											:label = "$ml.get('dashboard_pie_events_to')"
											v-model = "form.time.custom.to"
										></v-text-field>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>

					<v-col cols = "12" md = "6">
						<v-card outlined class = "fill-height">
							<v-card-title class = "caption text-uppercase py-2">
								<v-icon x-small class = "mr-1">mdi-cctv</v-icon>
								{{ $ml.get('dashboard_pie_events_section_devices') }}
							</v-card-title>
							<v-card-text class = "py-2">
								<v-select
									dense
									outlined
									multiple
									chips
									deletable-chips
									:items = "deviceItems"
									item-text = "label"
									item-value = "key"
									:label = "form.devices.length ? '' : $ml.get('dashboard_pie_events_devices')"
									small-chips
									v-model = "form.devices"
								></v-select>
							</v-card-text>
						</v-card>
					</v-col>

					<v-col cols = "12" md = "6">
						<v-card outlined class = "fill-height">
							<v-card-title class = "caption text-uppercase py-2">
								<v-icon x-small class = "mr-1">mdi-group</v-icon>
								{{ $ml.get('dashboard_pie_events_section_grouping') }}
							</v-card-title>
							<v-card-text class = "py-2">
								<v-select
									dense
									outlined
									multiple
									chips
									deletable-chips
									:disabled = "!groupableFieldItems.length"
									:items = "groupableFieldItems"
									item-text = "label"
									item-value = "key"
									:label = "form.groupBy.length ? '' : $ml.get('dashboard_pie_events_group_by')"
									small-chips
									v-model = "form.groupBy"
									@change = "normalizeGroupSelection"
								></v-select>
								<div v-if = "!groupableFieldItems.length" class = "caption text--secondary">
									{{ $ml.get('dashboard_pie_events_no_grouping') }}
								</div>
							</v-card-text>
						</v-card>
					</v-col>

					<v-col v-if = "isKpiChart" cols = "12" md = "6">
						<v-card outlined class = "fill-height">
							<v-card-title class = "caption text-uppercase py-2">
								<v-icon x-small class = "mr-1">mdi-palette</v-icon>
								{{ $ml.get('dashboard_pie_events_section_appearance') }}
							</v-card-title>
							<v-card-text class = "py-2">
								<v-row dense>
									<v-col cols = "12" md = "6">
										<v-select
											dense
											outlined
											:items = "kpiColorItems"
											item-text = "label"
											item-value = "value"
											:label = "$ml.get('dashboard_pie_events_kpi_color')"
											v-model = "form.appearance.color"
										></v-select>
									</v-col>
									<v-col cols = "12" md = "6">
										<v-select
											dense
											outlined
											:items = "kpiIconItems"
											item-text = "label"
											item-value = "value"
											:label = "$ml.get('dashboard_pie_events_kpi_icon')"
											v-model = "form.appearance.icon"
										></v-select>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>

					<v-col cols = "12">
						<v-card outlined>
							<v-card-title class = "caption text-uppercase py-2">
								<v-icon x-small class = "mr-1">mdi-filter-outline</v-icon>
								{{ $ml.get('dashboard_pie_events_section_filters') }}
								<v-spacer></v-spacer>
								<v-btn x-small color = "primary" :disabled = "!canAddFilter" @click = "addFilter">
									<v-icon x-small class = "mr-1">mdi-plus</v-icon>
									{{ $ml.get('dashboard_pie_events_add_filter') }}
								</v-btn>
							</v-card-title>
							<v-card-text class = "py-2">
								<div v-if = "!filterableFieldItems.length" class = "caption text--secondary">
									{{ $ml.get('dashboard_pie_events_no_filters') }}
								</div>
								<v-row dense v-for = "(filter, index) in form.filters" :key = "index">
									<v-col cols = "12" md = "4">
										<v-select
											dense
											outlined
											:items = "filterableFieldItems"
											item-text = "label"
											item-value = "key"
											:label = "$ml.get('dashboard_pie_events_filter_field')"
											v-model = "filter.field"
											@change = "normalizeFilterOperator(filter)"
										></v-select>
									</v-col>
									<v-col cols = "12" md = "3">
										<v-select
											dense
											outlined
											:items = "operatorItems(filter.field)"
											:label = "$ml.get('dashboard_pie_events_filter_operator')"
											v-model = "filter.operator"
										></v-select>
									</v-col>
									<v-col cols = "12" md = "4">
										<v-text-field
											dense
											outlined
											:label = "$ml.get('dashboard_pie_events_filter_value')"
											v-model = "filter.value"
										></v-text-field>
									</v-col>
									<v-col cols = "12" md = "1" class = "text-right">
										<v-btn icon small color = "error" @click = "removeFilter(index)">
											<v-icon small>mdi-delete</v-icon>
										</v-btn>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>

					<v-col cols = "12" md = "4">
						<v-card outlined class = "fill-height">
							<v-card-title class = "caption text-uppercase py-2">
								<v-icon x-small class = "mr-1">mdi-refresh</v-icon>
								{{ $ml.get('dashboard_pie_events_section_refresh') }}
							</v-card-title>
							<v-card-text class = "py-2">
								<v-text-field
									dense
									outlined
									type = "number"
									:min = "refreshBounds.min"
									:max = "refreshBounds.max"
									:label = "$ml.get('dashboard_pie_events_refresh_seconds')"
									v-model.number = "form.refreshSeconds"
								></v-text-field>
								<div class = "caption text--secondary">
									{{ refreshHelpText }}
								</div>
							</v-card-text>
						</v-card>
					</v-col>

					<v-col cols = "12" md = "8">
						<v-card outlined class = "fill-height">
							<v-card-title class = "caption text-uppercase py-2">
								<v-icon x-small class = "mr-1">mdi-chart-pie</v-icon>
								{{ $ml.get('dashboard_pie_events_section_preview') }}
								<v-spacer></v-spacer>
								<v-btn data-testid = "dashboard-pie-events-preview-button" x-small color = "primary" :loading = "previewLoading" :disabled = "!canPreview" @click = "preview">
									<v-icon x-small class = "mr-1">mdi-eye</v-icon>
									{{ $ml.get('dashboard_pie_events_preview') }}
								</v-btn>
							</v-card-title>
							<v-card-text class = "py-2">
								<v-alert v-if = "previewError" dense outlined type = "error" class = "caption mb-2">
									{{ previewErrorText }}
								</v-alert>
								<div v-if = "previewLoading" class = "caption text--secondary">
									{{ $ml.get('dashboard_pie_events_preview_loading') }}
								</div>
								<div v-else-if = "previewData && isKpiChart" class = "dashboard-pie-events-preview dashboard-pie-events-preview--kpi">
									<div class = "dashboard-pie-events-preview__kpi-card" :style = "kpiPreviewStyle">
										<v-icon color = "white" class = "dashboard-pie-events-preview__kpi-icon">{{ kpiAppearance.icon }}</v-icon>
										<div>
											<div class = "caption text-uppercase dashboard-pie-events-preview__kpi-label">{{ form.name || $ml.get('dashboard_pie_events_chart_type_kpi') }}</div>
											<strong>{{ previewKpiValue }}</strong>
											<div class = "caption dashboard-pie-events-preview__kpi-meta">{{ $ml.get('dashboard_pie_events_interval_total') }}</div>
										</div>
									</div>
								</div>
								<div v-else-if = "previewData && previewData.slices.length && canRenderChartPreview" class = "dashboard-pie-events-preview">
					<div data-testid = "dashboard-pie-events-preview-summary" class = "caption font-weight-bold mb-2 dashboard-pie-events-preview__summary">
						<span>{{ $ml.get('dashboard_pie_events_interval_total') }}: {{ previewIntervalTotal }}</span>
						<span v-if = "previewGroupedTotal !== null && previewGroupedTotal !== previewIntervalTotal">
							{{ $ml.get('dashboard_pie_events_grouped_total') }}: {{ previewGroupedTotal }}
						</span>
					</div>
									<v-row dense align = "stretch">
									<v-col cols = "12" md = "5">
										<div class = "dashboard-pie-events-preview__chart">
											<canvas ref = "previewCanvas"></canvas>
											<div v-if = "isPieLikeChart" data-testid = "dashboard-pie-events-chart-total" class = "dashboard-pie-events-preview__chart-total">
												<div>{{ $ml.get('dashboard_pie_events_interval_total') }}</div>
												<strong>{{ previewIntervalTotal }}</strong>
											</div>
										</div>
									</v-col>
										<v-col cols = "12" md = "7">
										<v-simple-table data-testid = "dashboard-pie-events-preview-table" dense class = "dashboard-pie-events-preview__table">
												<thead>
													<tr>
														<th>{{ $ml.get('dashboard_pie_events_slice') }}</th>
														<th class = "text-right">{{ $ml.get('dashboard_pie_events_value') }}</th>
														<th class = "text-right">{{ $ml.get('dashboard_pie_events_percent') }}</th>
													</tr>
												</thead>
												<tbody>
													<tr v-for = "(slice, index) in previewData.slices" :key = "`${slice.label}-${index}`" :data-testid = "`dashboard-pie-events-slice-row-${index}`">
														<td>
															<span class = "dashboard-pie-events-preview__swatch" :style = "{ backgroundColor: slice.color }"></span>
															<span :data-testid = "`dashboard-pie-events-slice-label-${index}`" class = "dashboard-pie-events-preview__label" :title = "slice.label">{{ slice.label }}</span>
														</td>
														<td :data-testid = "`dashboard-pie-events-slice-value-${index}`" class = "text-right">{{ slice.value }}</td>
														<td :data-testid = "`dashboard-pie-events-slice-percent-${index}`" class = "text-right">{{ slice.percent }}</td>
													</tr>
												</tbody>
											</v-simple-table>
										</v-col>
									</v-row>
									<v-alert v-if = "previewWarnings.length" dense outlined type = "warning" class = "caption mt-2 mb-0">
										<div v-for = "warning in previewWarnings" :key = "warning">{{ warning }}</div>
									</v-alert>
								</div>
								<div v-else class = "caption text--secondary">
									{{ $ml.get('dashboard_pie_events_preview_empty') }}
								</div>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
			</v-card-text>

			<v-divider></v-divider>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn data-testid = "dashboard-pie-events-add-button" small color = "primary" :disabled = "!canAdd" @click = "confirmAdd">
					{{ $ml.get('dashboard_pie_events_add') }}
				</v-btn>
				<v-btn small color = "error" @click = "close">
					{{ $ml.get('close') }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script type = "text/javascript">
	import Chart from 'chart.js'
	import DashboardChartsService from '@/services/dashboard-charts.service.js'
	import {
		DASHBOARD_PIE_EVENTS_DEFAULT_KPI_APPEARANCE,
		DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS,
		buildPieEventsStreamData,
		getDefaultPieEventsConfig,
		normalizePieEventsConfig,
	} from '@/services/dashboard-pie-events.config.js'

	const DEFAULT_COLORS = [
		'#7E57C2', '#42A5F5', '#26A69A', '#66BB6A', '#FFCA28',
		'#FFA726', '#EF5350', '#EC407A', '#AB47BC', '#78909C',
	];
	const KPI_COLOR_OPTIONS = ['#1E88E5', '#7E57C2', '#26A69A', '#66BB6A', '#FFA726', '#EF5350'];
	const KPI_ICON_OPTIONS = ['mdi-shield-check', 'mdi-counter', 'mdi-alert-circle-outline', 'mdi-cctv', 'mdi-account-check', 'mdi-car'];

	export default{
		name : 'DashboardPieEventsConfigDialog',
		props: {
			value: {
				type: Boolean,
				default: false,
			},
		},
		data(){
			return {
				metadata: null,
				form: this.buildDefaultForm(null),
				loadingMetadata: false,
				metadataError: false,
				previewLoading: false,
				previewError: false,
				previewData: null,
				previewWarnings: [],
				previewChart: null,
			}
		},
		computed:{
			metadataDefaults(){
				return this.metadata && this.metadata.defaults ? this.metadata.defaults : {};
			},
			metadataConstraints(){
				return this.metadata && this.metadata.constraints ? this.metadata.constraints : {};
			},
			datasourceItems(){
				return this.toSelectItems(this.metadata ? this.metadata.datasources : []);
			},
			selectedDatasource(){
				return this.findByKey(this.metadata ? this.metadata.datasources : [], this.form.datasource);
			},
			tableItems(){
				const datasource = this.selectedDatasource;
				if (datasource && Array.isArray(datasource.tables)) return this.toSelectItems(datasource.tables);
				return this.toSelectItems(this.metadata ? this.metadata.tables : []);
			},
			fieldItems(){
				const datasource = this.selectedDatasource;
				const datasourceFields = datasource && (datasource.fields || datasource.fieldItems || datasource.columns);
				const fields = datasourceFields || (this.metadata ? this.metadata.fields : []);
				return this.toSelectItems(fields);
			},
			groupableFieldItems(){
				return this.fieldItems.filter(field => field.groupable && !this.isImageField(field));
			},
			filterableFieldItems(){
				return this.fieldItems.filter(field => field.filterable);
			},
			deviceItems(){
				const devices = this.metadata ? this.metadata.devices : [];
				return this.toSelectItems(devices && Array.isArray(devices.items) ? devices.items : devices);
			},
			intervalPresetItems(){
				const source = this.metadata && (this.metadata.intervalPresets || this.metadata.presets)
					? (this.metadata.intervalPresets || this.metadata.presets)
					: [
						{ key: 'today', label: this.$ml.get('dashboard_pie_events_preset_today') },
						{ key: 'last_24h', label: this.$ml.get('dashboard_pie_events_preset_last_24h') },
						{ key: 'last_7d', label: this.$ml.get('dashboard_pie_events_preset_last_7d') },
						{ key: 'custom', label: this.$ml.get('dashboard_pie_events_preset_custom') },
					];
				const items = this.toSelectItems(source);
				if (!items.some(item => item.key === 'custom')) {
					items.push({ key: 'custom', label: this.$ml.get('dashboard_pie_events_preset_custom') });
				}
				return items;
			},
				chartTypeItems(){
				return [
					{ key: 'pie', label: this.$ml.get('dashboard_pie_events_chart_type_pie') },
					{ key: 'doughnut', label: this.$ml.get('dashboard_pie_events_chart_type_doughnut') },
					{ key: 'kpi', label: this.$ml.get('dashboard_pie_events_chart_type_kpi') },
					{ key: 'line', label: this.$ml.get('dashboard_pie_events_chart_type_line') },
				];
			},
			kpiColorItems(){
				return KPI_COLOR_OPTIONS.map((value, index) => ({
					value,
					label: `${this.$ml.get('dashboard_pie_events_kpi_color')} ${index + 1}`,
				}));
			},
			kpiIconItems(){
				return KPI_ICON_OPTIONS.map(value => ({
					value,
					label: value.replace('mdi-', '').replace(/-/g, ' '),
				}));
			},
			isPieLikeChart(){
				return ['pie', 'doughnut'].includes(this.form.chartType);
			},
			isKpiChart(){
				return this.form.chartType === 'kpi';
			},
			isLineChart(){
				return this.form.chartType === 'line';
			},
			canRenderChartPreview(){
				return this.isPieLikeChart || this.isLineChart;
			},
			kpiAppearance(){
				const appearance = this.form && this.form.appearance ? this.form.appearance : {};
				return {
					color: appearance.color || DASHBOARD_PIE_EVENTS_DEFAULT_KPI_APPEARANCE.color,
					icon: appearance.icon || DASHBOARD_PIE_EVENTS_DEFAULT_KPI_APPEARANCE.icon,
				};
			},
			kpiPreviewStyle(){
				return {
					background: `linear-gradient(135deg, ${this.kpiAppearance.color}, #0F253D)`,
				};
			},
			isCustomRange(){
				return this.form.time && this.form.time.preset === 'custom';
			},
			refreshBounds(){
				return {
					min: this.metadataConstraints.minRefreshSeconds || 30,
					max: this.metadataConstraints.maxRefreshSeconds || 3600,
				};
			},
			refreshHelpText(){
				return `${this.$ml.get('dashboard_pie_events_refresh_range')} ${this.refreshBounds.min} - ${this.refreshBounds.max}`;
			},
			canAddFilter(){
				const maxFilters = this.metadataConstraints.maxFilters || 5;
				return this.filterableFieldItems.length > 0 && this.form.filters.length < maxFilters;
			},
			validationErrors(){
				const errors = [];
				if (!this.form.chartType) errors.push('dashboard_pie_events_validation_chart_type');
				if (!this.form.datasource) errors.push('dashboard_pie_events_validation_datasource');
				if (!this.form.devices.length) errors.push('dashboard_pie_events_validation_devices');
				if (this.isCustomRange) {
					if (!this.form.time.custom.from || !this.form.time.custom.to) errors.push('dashboard_pie_events_validation_custom_required');
					if (this.form.time.custom.from && this.form.time.custom.to && new Date(this.form.time.custom.from) >= new Date(this.form.time.custom.to)) {
						errors.push('dashboard_pie_events_validation_custom_order');
					}
					if (this.customRangeExceedsLimit()) errors.push('dashboard_pie_events_validation_custom_limit');
				}
				if (!this.refreshIsValid()) errors.push('dashboard_pie_events_validation_refresh');
				if (!this.groupingIsValid()) errors.push('dashboard_pie_events_validation_grouping');
				if (!this.filtersAreValid()) errors.push('dashboard_pie_events_validation_filters');
				return errors;
			},
			canPreview(){
				return !this.loadingMetadata && !this.previewLoading && !this.validationErrors.length;
			},
			canAdd(){
				return this.canPreview && !!this.previewData && !this.previewError;
			},
			previewIntervalTotal(){
				if (!this.previewData) return 0;
				return this.normalizeTotalValue(this.firstAvailableTotal(this.previewData, [
					'requestedTotal',
					'requested_total',
					'total_requested',
					'totalRequested',
					'intervalTotal',
					'interval_total',
					'total',
				]));
			},
			previewGroupedTotal(){
				if (!this.previewData) return null;
				return this.normalizeTotalValue(this.firstAvailableTotal(this.previewData, [
					'visibleTotal',
					'visible_total',
					'groupedTotal',
					'grouped_total',
					'foundTotal',
					'found_total',
				]), null);
			},
			previewKpiValue(){
				return this.previewIntervalTotal;
			},
			previewErrorText(){
				if (!this.previewError) return '';
				return this.$ml.get('dashboard_pie_events_preview_error');
			},
		},
		watch:{
			value(val){
				if (val) this.loadMetadata();
				else this.destroyPreviewChart();
			},
			form: {
				handler(){
					this.resetPreview();
				},
				deep: true,
			},
			'form.datasource'(){
				this.syncTableSelection();
				this.syncFieldSelections();
			},
			'form.groupBy'(value){
				this.normalizeGroupSelection(value);
			},
		},
		beforeDestroy(){
			this.destroyPreviewChart();
		},
		methods:{
			buildDefaultForm(metadata){
				const form = normalizePieEventsConfig(getDefaultPieEventsConfig(metadata), metadata);
				const datasources = this.toSelectItems(metadata ? metadata.datasources : []);
				if (!form.datasource && datasources.length) form.datasource = datasources[0].key;
				form.groupBy = Array.isArray(form.groupBy) ? form.groupBy : (form.groupBy ? [form.groupBy] : []);
				return form;
			},
			updateValue(value){
				this.$emit('input', value);
			},
			close(){
				this.$emit('input', false);
				this.$emit('close');
			},
			resetPreview(){
				this.previewError = false;
				this.previewData = null;
				this.previewWarnings = [];
				this.destroyPreviewChart();
			},
			loadMetadata(){
				this.loadingMetadata = true;
				this.metadataError = false;

				DashboardChartsService.getPieEventsMetadata().then((response) => {
					if (response && (response.isAxiosError || response.response)) {
						this.metadata = null;
						this.metadataError = true;
						this.form = this.buildDefaultForm(null);
						return;
					}
					this.metadata = this.unwrapResponse(response);
					if (!this.metadata) this.metadataError = true;
					this.form = this.buildDefaultForm(this.metadata);
					this.syncTableSelection();
				}).catch(() => {
					this.metadataError = true;
					this.form = this.buildDefaultForm(null);
				}).finally(() => {
					this.loadingMetadata = false;
				});
			},
			unwrapResponse(response){
				if (!response) return null;
				if (response.data && response.data.data) return response.data.data;
				if (response.data) return response.data;
				return response;
			},
			toSelectItems(source){
				if (source && Array.isArray(source.items)) return this.toSelectItems(source.items);
				if (!Array.isArray(source)) return [];
				return source.map((item) => {
					if (typeof item === 'string') return { key: item, label: this.resolveMlLabel(item) };
					const key = item.key || item.id || item.value || item.deviceId || item.name;
					const rawLabel = item.label || item.name || item.text || key;
					const label = this.resolveMlLabel(rawLabel);
					return Object.assign({}, item, { key, label });
				}).filter(item => item.key);
			},
			resolveMlLabel(value){
				if (!value) return value;

				const key = `${value}`;
				let translationKey = key;
				if (key.indexOf('dashboard.pie.events.') === 0) {
					translationKey = key.replace(/[^a-zA-Z0-9_]/g, '_');
				} else if (key === 'event_type') {
					translationKey = 'dashboard_pie_events_fields_event_type';
				}

				if (translationKey.indexOf('dashboard_pie_events_') !== 0) return this.humanizeMetadataLabel(key);

				try {
					if (this.$ml && typeof this.$ml.get === 'function') {
						const translated = this.$ml.get(translationKey);
						if (translated && translated !== translationKey) return translated;
					}
				} catch (error) {
					// Fall through to a safe human label; labels should never break render.
				}

				return this.humanizeMetadataLabel(key);
			},
			humanizeMetadataLabel(value){
				if (!value) return value;
				let label = `${value}`;

				if (label.indexOf('dashboard.pie.events.') === 0) {
					const parts = label.split('.');
					label = parts[parts.length - 1];
				}

				const aliases = {
					event_type: this.translateMetadataKey('dashboard_pie_events_fields_event_type', 'Tipo de evento'),
					motive_categorie: 'Categoría de motivo',
					tag: 'Etiqueta',
					isalert: 'Alerta',
					deviceId: this.translateMetadataKey('dashboard_pie_events_fields_device', 'Dispositivo'),
					device_id: this.translateMetadataKey('dashboard_pie_events_fields_device', 'Dispositivo'),
					plate_chars: 'Caracteres de placa',
				};
				if (aliases[label]) return aliases[label];

				return label
					.replace(/([a-z])([A-Z])/g, '$1 $2')
					.replace(/[_-]+/g, ' ')
					.replace(/\s+/g, ' ')
					.trim()
					.replace(/^./, char => char.toUpperCase());
			},
			translateMetadataKey(key, fallback){
				try {
					if (this.$ml && typeof this.$ml.get === 'function') {
						const translated = this.$ml.get(key);
						if (translated && translated !== key) return translated;
					}
				} catch (error) {
					// Ignore translation failures for metadata labels.
				}
				return fallback;
			},
			isImageField(field){
				if (!field) return false;
				const values = [field.key, field.value, field.id, field.name, field.label, field.labelKey, field.type]
					.filter(Boolean)
					.map(value => `${value}`.toLowerCase());
				return values.some(value => {
					return value.includes('image')
						|| value.includes('img')
						|| value.includes('photo')
						|| value.includes('picture')
						|| value.includes('thumbnail')
						|| value.includes('evidence')
						|| value.includes('face_detected')
						|| value.includes('segment_photo')
						|| value.includes('full_photo');
				});
			},
			findByKey(source, key){
				return this.toSelectItems(source).find(item => item.key === key);
			},
			syncFieldSelections(){
				const groupableKeys = this.groupableFieldItems.map(item => item.key);
				this.form.groupBy = this.form.groupBy.filter(field => groupableKeys.includes(field));
				this.normalizeGroupSelection(this.form.groupBy);

				const filterableKeys = this.filterableFieldItems.map(item => item.key);
				this.form.filters = this.form.filters.filter(filter => filterableKeys.includes(filter.field));
			},
			normalizeGroupSelection(value){
				const selected = Array.isArray(value) ? value.filter(Boolean) : [];
				const allowed = this.groupableFieldItems.map(item => item.key);
				const maxGroups = this.metadataConstraints.maxGroupBy || this.metadataConstraints.maxGroups || 2;
				const normalized = selected
					.filter(field => allowed.includes(field))
					.filter((field, index, values) => values.indexOf(field) === index)
					.slice(-maxGroups);

				if (normalized.length !== selected.length || normalized.some((field, index) => field !== selected[index])) {
					this.form.groupBy = normalized;
				}
			},
			syncTableSelection(){
				if (!this.tableItems.length) {
					this.form.table = null;
					return;
				}
				if (!this.tableItems.some(item => item.key === this.form.table)) {
					this.form.table = this.tableItems[0].key;
				}
			},
			operatorItems(fieldKey){
				const field = this.filterableFieldItems.find(item => item.key === fieldKey);
				const operators = field && Array.isArray(field.operators) && field.operators.length ? field.operators : ['eq'];
				return operators;
			},
			normalizeFilterOperator(filter){
				const operators = this.operatorItems(filter.field);
				if (!operators.includes(filter.operator)) filter.operator = operators[0] || 'eq';
			},
			addFilter(){
				if (!this.canAddFilter) return;
				const field = this.filterableFieldItems[0];
				const operator = field && Array.isArray(field.operators) && field.operators.length ? field.operators[0] : 'eq';
				this.form.filters.push({ field: field ? field.key : null, operator, value: '' });
			},
			removeFilter(index){
				this.form.filters.splice(index, 1);
			},
			customRangeExceedsLimit(){
				const maxHours = this.metadataConstraints.maxCustomRangeHours || this.metadataConstraints.maxRangeHours;
				const maxDays = this.metadataConstraints.maxCustomWindowDays || this.metadataConstraints.maxWindowDays;
				if ((!maxHours && !maxDays) || !this.form.time.custom.from || !this.form.time.custom.to) return false;
				const diff = new Date(this.form.time.custom.to).getTime() - new Date(this.form.time.custom.from).getTime();
				const limitMs = maxHours
					? maxHours * 60 * 60 * 1000
					: maxDays * 24 * 60 * 60 * 1000;
				return diff > limitMs;
			},
			refreshIsValid(){
				const value = Number(this.form.refreshSeconds);
				return value >= this.refreshBounds.min && value <= this.refreshBounds.max;
			},
			groupingIsValid(){
				const allowed = this.groupableFieldItems.map(item => item.key);
				const maxGroups = this.metadataConstraints.maxGroupBy || this.metadataConstraints.maxGroups || 2;
				return this.form.groupBy.length <= maxGroups && this.form.groupBy.every(field => allowed.includes(field));
			},
			filtersAreValid(){
				const allowed = this.filterableFieldItems.map(item => item.key);
				return this.form.filters.every((filter) => {
					return filter.field
						&& allowed.includes(filter.field)
						&& this.operatorItems(filter.field).includes(filter.operator)
						&& filter.value !== null
						&& `${filter.value}`.length > 0;
				});
			},
			getConfig(){
				const config = normalizePieEventsConfig(this.form, this.metadata);
				if (!config.refreshSeconds) config.refreshSeconds = DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS;
				config.name = config.name ? `${config.name}`.trim() : '';
				config.title = config.name || config.title;
				if (config.chartType !== 'kpi') delete config.appearance;
				return config;
			},
			preview(){
				if (!this.canPreview) return;
				this.previewLoading = true;
				this.previewError = false;
				this.previewData = null;
				this.previewWarnings = [];

				return DashboardChartsService.previewPieEvents(this.getConfig()).then((response) => {
					if (response && (response.isAxiosError || response.response)) {
						this.previewError = true;
						return;
					}
					const data = this.unwrapResponse(response);
					this.previewData = this.normalizePreviewResponse(data);
					this.previewWarnings = Array.isArray(data && data.warnings) ? data.warnings : [];
					if (!this.previewData) this.previewError = true;
				}).catch(() => {
					this.previewError = true;
				}).finally(() => {
					this.previewLoading = false;
					if (this.previewData && this.previewData.slices.length && this.canRenderChartPreview) this.$nextTick(this.renderPreviewChart);
				});
			},
			normalizePreviewResponse(data){
				const chart = data && data.chart ? data.chart : data;
				if (!chart) return { slices: [], total: 0, visible_total: 0 };
				const linePoints = this.isLineChart ? this.extractLinePoints(chart) : [];
				if (linePoints.length) {
					const visibleTotal = linePoints.reduce((sum, point) => sum + point.value, 0);
					return Object.assign({ visible_total: visibleTotal }, this.extractPreviewTotals(chart), {
						slices: linePoints.map((point, index) => ({
							label: point.label || `${this.$ml.get('dashboard_pie_events_slice')} ${index + 1}`,
							value: point.value,
							percent: 0,
							color: DEFAULT_COLORS[index % DEFAULT_COLORS.length],
						})),
						total: chart.total || visibleTotal,
					});
				}
				if (Array.isArray(chart.slices)) {
					const values = chart.slices.map(slice => Number(slice.value || slice.count || slice.total || 0));
					const visibleTotal = values.reduce((sum, value) => sum + value, 0);
					const total = chart.total || visibleTotal;
					return Object.assign({ visible_total: visibleTotal }, this.extractPreviewTotals(chart), {
						slices: chart.slices.map((slice, index) => ({
							label: this.resolvePreviewSliceLabel(slice, index),
							value: values[index],
							percent: slice.percent || (total ? Math.round((values[index] / total) * 10000) / 100 : 0),
							color: slice.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
						})),
						total,
					});
				}
				const labels = Array.isArray(chart.labels) ? chart.labels : [];
				const values = Array.isArray(chart.values) ? chart.values : [];
				const visibleTotal = values.reduce((sum, value) => sum + Number(value || 0), 0);
				const total = chart.total || visibleTotal;
				return Object.assign({ visible_total: visibleTotal }, this.extractPreviewTotals(chart), {
					slices: labels.map((label, index) => {
						const value = Number(values[index] || 0);
						return {
							label: this.resolvePreviewSliceLabel({ label }, index),
							value,
							percent: total ? Math.round((value / total) * 10000) / 100 : 0,
							color: DEFAULT_COLORS[index % DEFAULT_COLORS.length],
						};
					}),
					total,
				});
			},
			extractLinePoints(chart){
				const sources = [];
				const timeseries = chart.timeseries || chart.timeSeries;
				if (Array.isArray(timeseries)) sources.push(timeseries);
				else if (timeseries && typeof timeseries === 'object') {
					if (Array.isArray(timeseries.points)) sources.push({ labels: timeseries.labels, points: timeseries.points });
					if (Array.isArray(timeseries.series) && timeseries.series[0] && Array.isArray(timeseries.series[0].points)) {
						sources.push({ labels: timeseries.labels, points: timeseries.series[0].points });
					}
				}
				if (Array.isArray(chart.points)) sources.push(chart.points);
				if (Array.isArray(chart.series) && chart.series[0] && Array.isArray(chart.series[0].points)) sources.push({ labels: chart.labels, points: chart.series[0].points });
				if (Array.isArray(chart.labels) && Array.isArray(chart.values)) sources.push({ labels: chart.labels, points: chart.values });

				for (const source of sources) {
					const normalized = this.normalizeLinePointSource(source);
					if (normalized.length) return normalized;
				}
				return [];
			},
			normalizeLinePointSource(source){
				const labels = source && !Array.isArray(source) && Array.isArray(source.labels) ? source.labels : [];
				const points = source && !Array.isArray(source) && Array.isArray(source.points) ? source.points : source;
				if (!Array.isArray(points)) return [];

				return points.map((point, index) => {
					if (Array.isArray(point)) {
						return { label: point[0] || labels[index] || `${index + 1}`, value: Number(point[1] || 0) };
					}
					if (point && typeof point === 'object') {
						const label = point.label || point.time || point.timestamp || point.date || point.bucket || labels[index] || `${index + 1}`;
						const rawValue = point.value !== undefined ? point.value : (point.count !== undefined ? point.count : point.total);
						return { label, value: Number(rawValue || 0) };
					}
					return { label: labels[index] || `${index + 1}`, value: Number(point || 0) };
				}).filter(point => point.label !== undefined && point.label !== null);
			},
			extractPreviewTotals(chart){
				if (!chart) return {};
				const keys = ['requestedTotal', 'requested_total', 'total_requested', 'totalRequested', 'intervalTotal', 'interval_total', 'visibleTotal', 'visible_total', 'groupedTotal', 'grouped_total', 'foundTotal', 'found_total'];
				return keys.reduce((totals, key) => {
					if (chart[key] !== undefined && chart[key] !== null && chart[key] !== '') totals[key] = chart[key];
					return totals;
				}, {});
			},
			firstAvailableTotal(source, keys){
				return keys.map(key => source[key]).find(value => value !== undefined && value !== null && value !== '');
			},
			normalizeTotalValue(value, fallback = 0){
				if (value === undefined || value === null || value === '') return fallback;
				const numeric = Number(value);
				return Number.isFinite(numeric) ? numeric : value;
			},
			resolvePreviewSliceLabel(slice, index){
				const groupedValue = this.resolveGroupedSliceLabel(slice);
				if (groupedValue) return groupedValue;

				const value = slice && (slice.label || slice.name || slice.key || slice.category || slice.name_categorie || slice.motive_categorie || slice.event_type || slice.group || slice.group_by);
				if (value !== undefined && value !== null && `${value}`.trim()) return `${value}`;
				return `${this.$ml.get('dashboard_pie_events_slice')} ${index + 1}`;
			},
			resolveGroupedSliceLabel(slice){
				if (!slice) return '';

				const groupBy = Array.isArray(this.form.groupBy) ? this.form.groupBy : [];
				const groupSource = slice.group || slice.group_by || slice.groups || slice.keys || slice.dimensions || slice.values;
				const parts = groupBy.map((field, index) => {
					let value;
					if (slice[field] !== undefined) value = slice[field];
					else if (groupSource && Array.isArray(groupSource)) value = groupSource[index];
					else if (groupSource && typeof groupSource === 'object') value = groupSource[field];

					if (value === undefined || value === null || value === '') return null;
					return `${this.humanizeMetadataLabel(field)}: ${this.stringifyGroupValue(value)}`;
				}).filter(Boolean);

				if (parts.length > 0) return parts.join(' / ');
				if (Array.isArray(groupSource)) return groupSource.map(value => this.stringifyGroupValue(value)).filter(Boolean).join(' / ');
				if (groupSource && typeof groupSource === 'object') {
					return Object.keys(groupSource).map(key => `${this.humanizeMetadataLabel(key)}: ${this.stringifyGroupValue(groupSource[key])}`).filter(Boolean).join(' / ');
				}

				return '';
			},
			stringifyGroupValue(value){
				if (value === undefined || value === null) return '';
				if (typeof value === 'boolean') return value ? 'Sí' : 'No';
				if (typeof value === 'object') return value.label || value.name || value.key || JSON.stringify(value);
				return `${value}`;
			},
			renderPreviewChart(){
				this.destroyPreviewChart();
				if (!this.previewData || !this.previewData.slices.length || !this.$refs.previewCanvas) return;
				const labels = this.previewData.slices.map(slice => slice.label);
				const values = this.previewData.slices.map(slice => Number(slice.value || 0));
				const colors = this.previewData.slices.map((slice, index) => slice.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]);
				const ctx = this.$refs.previewCanvas.getContext('2d');
				this.previewChart = new Chart(ctx, {
					type: this.resolvePreviewChartType(),
					data: {
						labels,
						datasets: [{
							data: values,
							backgroundColor: this.isLineChart ? 'rgba(126, 87, 194, 0.16)' : colors,
							borderColor: this.isLineChart ? '#7E57C2' : '#1E1E1E',
							borderWidth: 2,
							pointBackgroundColor: this.isLineChart ? '#42A5F5' : colors,
							pointBorderColor: '#FFFFFF',
							fill: this.isLineChart,
						}],
					},
					options: this.buildPreviewChartOptions(),
				});
			},
			resolvePreviewChartType(){
				if (this.form.chartType === 'line') return 'line';
				return this.form.chartType === 'doughnut' ? 'doughnut' : 'pie';
			},
			buildPreviewChartOptions(){
				const options = {
						responsive: true,
						maintainAspectRatio: false,
						legend: {
							display: false,
							labels: { fontColor: '#FFFFFF', boxWidth: 10 },
						},
						tooltips: {
							backgroundColor: '#424242',
							titleFontColor: '#FFFFFF',
							bodyFontColor: '#FFFFFF',
						},
					};
				if (this.isLineChart) {
					options.scales = {
						xAxes: [{ ticks: { fontColor: '#FFFFFF' }, gridLines: { color: 'rgba(255,255,255,0.12)' } }],
						yAxes: [{ ticks: { beginAtZero: true, fontColor: '#FFFFFF' }, gridLines: { color: 'rgba(255,255,255,0.12)' } }],
					};
				}
				return options;
			},
			destroyPreviewChart(){
				if (this.previewChart) {
					this.previewChart.destroy();
					this.previewChart = null;
				}
			},
			confirmAdd(){
				if (!this.canAdd) return;
				const config = this.getConfig();
				const streamData = buildPieEventsStreamData(config, this.previewData);
				this.$emit('add', {
					key: 'pie_events',
					from: 'charts',
					component: this.resolveWidgetComponent(config.chartType),
					stream_data: streamData,
					config,
					preview: this.previewData,
				});
				this.close();
			},
			resolveWidgetComponent(chartType){
				return chartType === 'kpi'
					? 'dashboard_kpi_events_widget'
					: 'dashboard_pie_events_widget';
			},
		},
	}
</script>

<style scoped>
.dashboard-pie-events-preview__summary {
	display: flex;
	flex-wrap: wrap;
	gap: 8px 16px;
}

.dashboard-pie-events-preview__chart {
	min-height: 240px;
	height: 240px;
	position: relative;
}

.dashboard-pie-events-preview__kpi-card {
	display: flex;
	align-items: center;
	gap: 16px;
	min-height: 160px;
	padding: 20px;
	border-radius: 10px;
	background: linear-gradient(135deg, #1E88E5, #1565C0);
	color: #FFFFFF;
	box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.16);
}

.dashboard-pie-events-preview__kpi-icon {
	width: 52px;
	height: 52px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.16);
}

.dashboard-pie-events-preview__kpi-card strong {
	display: block;
	font-size: 42px;
	line-height: 1;
	font-variant-numeric: tabular-nums;
}

.dashboard-pie-events-preview__kpi-label,
.dashboard-pie-events-preview__kpi-meta {
	color: rgba(255, 255, 255, 0.82);
}

.dashboard-pie-events-preview__chart-total {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	min-width: 92px;
	padding: 8px 10px;
	border-radius: 999px;
	background: rgba(30, 30, 30, 0.78);
	box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.16);
	color: #FFFFFF;
	font-size: 10px;
	line-height: 1.2;
	text-align: center;
	pointer-events: none;
}

.dashboard-pie-events-preview__chart-total strong {
	display: block;
	font-size: 18px;
	line-height: 1.1;
}

.dashboard-pie-events-preview__chart canvas {
	display: block;
	min-height: 240px;
	width: 100% !important;
	height: 240px !important;
}

.dashboard-pie-events-preview__table {
	table-layout: fixed;
	width: 100%;
}

.dashboard-pie-events-preview__table th:first-child,
.dashboard-pie-events-preview__table td:first-child {
	width: calc(100% - 130px);
	max-width: 0;
}

.dashboard-pie-events-preview__table th:nth-child(2),
.dashboard-pie-events-preview__table td:nth-child(2) {
	width: 64px;
	white-space: nowrap;
}

.dashboard-pie-events-preview__table th:nth-child(3),
.dashboard-pie-events-preview__table td:nth-child(3) {
	width: 66px;
	white-space: nowrap;
}

.dashboard-pie-events-preview__label {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	word-break: break-word;
}

.dashboard-pie-events-preview__swatch {
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	margin-right: 6px;
	vertical-align: middle;
}
</style>
